import React, { useMemo, useState } from 'react';
import { App, Button, Card, Form, Input, Popconfirm, Row, Switch, Table, Typography } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../authConfig';
import { GetAccessToken } from '../../utils/auth-utils';
import { HandoverSystemDto } from '../../dto/lost-reason-models';
import {
  permanentDeleteHandoverSystem,
  saveHandoverSystem,
  setHandoverSystemActive,
} from '../../services/lost-reason-service';

const { Title, Text } = Typography;

export type HandoverSystemsTabProps = {
  handoverSystems: HandoverSystemDto[];
  onRefresh: () => Promise<void>;
};

export const HandoverSystemsTab: React.FC<HandoverSystemsTabProps> = ({ handoverSystems, onRefresh }) => {
  const { instance, inProgress } = useMsal();
  const { notification } = App.useApp();
  const [editing, setEditing] = useState<HandoverSystemDto | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form] = Form.useForm();

  const sorted = useMemo(
    () =>
      [...handoverSystems].sort((a, b) =>
        a.description.localeCompare(b.description, undefined, { sensitivity: 'base' }),
      ),
    [handoverSystems],
  );

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  const openNew = () => {
    setEditing({ id: '', keyword: '', description: '', isActive: true });
    form.setFieldsValue({ keyword: '', description: '', isActive: true });
    setShowForm(true);
  };

  const openEdit = (h: HandoverSystemDto) => {
    setEditing({ ...h });
    form.setFieldsValue({ keyword: h.keyword, description: h.description, isActive: h.isActive });
    setShowForm(true);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const res = await saveHandoverSystem(token.accessToken, {
        id: editing?.id || null,
        keyword: values.keyword.trim(),
        description: values.description.trim(),
        isActive: values.isActive,
      });
      if (res.isSuccess) {
        notification.success({ message: 'Handover system saved' });
        closeForm();
        await onRefresh();
      } else {
        notification.error({ message: res.errors?.join(', ') ?? 'Save failed' });
      }
    } catch {
    }
  };

  const onDeactivate = async (h: HandoverSystemDto) => {
    if (!h.id) return;
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await setHandoverSystemActive(token.accessToken, h.id, false);
    if (res.isSuccess) {
      notification.success({ message: 'Handover system deactivated' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Deactivate failed' });
    }
  };

  const onDelete = async (h: HandoverSystemDto) => {
    if (!h.id) return;
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await permanentDeleteHandoverSystem(token.accessToken, h.id);
    if (res.isSuccess) {
      notification.success({ message: 'Handover system deleted' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Delete failed' });
    }
  };

  const columns = [
    { title: 'Keyword (Lead Handling)', dataIndex: 'keyword', key: 'kw', width: 240 },
    { title: 'Partner name (shown to bookers)', dataIndex: 'description', key: 'desc' },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'active',
      width: 80,
      render: (v: boolean) => (v ? 'Yes' : 'No'),
    },
    {
      title: '',
      key: 'a',
      width: 300,
      render: (_: unknown, h: HandoverSystemDto) => (
        <>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(h)}>
            Edit
          </Button>
          {h.isActive && (
            <Button size="small" style={{ marginLeft: 8 }} onClick={() => void onDeactivate(h)}>
              Deactivate
            </Button>
          )}
          <Popconfirm
            title="Delete handover system?"
            description="Groups still referencing this keyword will no longer resolve a partner name."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => void onDelete(h)}
          >
            <Button size="small" danger style={{ marginLeft: 8 }}>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div
      className="container flex-admin-vertical-container"
      style={{ width: '100%', maxWidth: 1100, minWidth: 0, justifyContent: 'flex-start' }}
    >
      <Row style={{ marginBottom: 8, flexDirection: 'column' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Handover systems
        </Title>
        <Text type="secondary">
          Target systems a lost lead can be handed over to. The keyword is forwarded to Lead Handling (e.g. pv_to_hp);
          the description is the partner name for reference. Bind a system to a channel outcome group with the
          &quot;Handover to other system&quot; outcome.
        </Text>
      </Row>

      {showForm && editing ? (
        <Card style={{ marginBottom: 24 }}>
          <Title level={5}>{editing.id ? 'Edit handover system' : 'New handover system'}</Title>
          <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
            <Form.Item
              name="keyword"
              label="Keyword (handover type sent to Lead Handling)"
              rules={[{ required: true, message: 'Keyword is required (e.g. pv_to_hp).' }]}
            >
              <Input placeholder="e.g. pv_to_hp" />
            </Form.Item>
            <Form.Item
              name="description"
              label="Partner name (shown to bookers)"
              rules={[{ required: true, message: 'Partner name is required.' }]}
            >
              <Input placeholder="e.g. Heat Pre-Sales" />
            </Form.Item>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Button type="primary" onClick={() => void onSubmit()}>
              Save
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => closeForm()}>
              Cancel
            </Button>
          </Form>
        </Card>
      ) : null}

      <Row justify="end" style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew} disabled={showForm}>
          Add handover system
        </Button>
      </Row>

      <Table rowKey="id" size="small" columns={columns} dataSource={sorted} pagination={false} scroll={{ x: 'max-content' }} />
    </div>
  );
};
